import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout/Layout";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import CollectionsHeader from "../../components/ProductCollections/CollectionsHeader";
import CategoryTabs from "../../components/ProductCollections/CategoryTabs";
import ProductGrid from "../../components/ProductCollections/ProductGrid";
import SelectionBar from "../../components/ProductCollections/SelectionBar";
import LoadingState from "../../components/Common/LoadingState";
import { useProductCollectionsData } from "../../hooks/useProductCollectionsData";

const ProductCollectionsIndex = () => {
    const { t } = useTranslation();
    const {
        activeCategory,
        handleCategorySelect,
        currentPage,
        totalPages,
        handlePageChange,
        selectedIds,
        categories,
        products,
        isLoadingCategories,
        isLoadingProducts,
        hasLoadedCategories,
        hasLoadedProducts,
        handleToggle,
        openCheckout,
        maxProducts,
        orderBanner,
    } = useProductCollectionsData();

    return (
        <Layout activeLabel="Product collections">
            <div>
                <CollectionsHeader
                    title={t("PRODUCT_COLLECTIONS_TITLE")}
                    subtitle={t("PRODUCT_COLLECTIONS_SUBTITLE", { count: maxProducts })}
                    actionLabel={`${t("PRODUCT_COLLECTIONS_SEND_GIFTS")} ${selectedIds.length}/${maxProducts}`}
                    onAction={openCheckout}
                    actionDisabled={selectedIds.length === 0 || !orderBanner.canOrder}
                />


                {isLoadingCategories && !hasLoadedCategories ? (
                    <LoadingState message={t("COMMON_LOADING_DATA")} />
                ) : (
                    <>
                        <CategoryTabs
                            categories={categories}
                            activeCategory={activeCategory}
                            onSelect={handleCategorySelect}
                        />
                        {!hasLoadedProducts ? (
                            <div className="mt-8 flex min-h-[260px] items-center justify-center rounded-md border border-slate-200 bg-input-bg">
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                    <span>{t("PRODUCT_COLLECTIONS_LOADING_PRODUCTS")}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="relative">
                                {products.length ? (
                                    <>
                                        <div className={`transition-opacity duration-150 ${isLoadingProducts || !orderBanner.canOrder ? "opacity-50 pointer-events-none" : ""}`}>
                                            <ProductGrid
                                                products={products}
                                                selectedIds={selectedIds}
                                                onToggle={handleToggle}
                                            />
                                        </div>
                                        {totalPages > 1 && (
                                            <div className="mt-6 flex items-center justify-center gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    disabled={currentPage <= 1 || isLoadingProducts}
                                                    className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none"
                                                >
                                                    <ChevronLeft className="h-4 w-4" />
                                                </button>
                                                <span className="text-xs text-slate-500">
                                                    {t("COMMON_PAGE")} {currentPage} {t("COMMON_OF")} {totalPages}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    disabled={currentPage >= totalPages || isLoadingProducts}
                                                    className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none"
                                                >
                                                    <ChevronRight className="h-4 w-4" />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="mt-8 rounded-md border border-slate-200 bg-input-bg px-6 py-12 text-center sm:py-16">
                                        <p className="text-sm text-slate-500">{t("PRODUCT_COLLECTIONS_EMPTY_CATEGORY")}</p>
                                    </div>
                                )}
                                {isLoadingProducts && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-md text-sm text-slate-500">
                                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                            <span>{t("COMMON_LOADING")}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <SelectionBar
                            selectedCount={selectedIds.length}
                            maxCount={maxProducts}
                            onSubmit={openCheckout}
                            disabled={selectedIds.length === 0 || !orderBanner.canOrder}
                        />
                    </>
                )}
            </div>

        </Layout>
    );
};

export default ProductCollectionsIndex;
